import { JournalEvent } from '../journal';

import { TvpilJournalCategory } from './categories';
import { TvpilJournalEvent } from './events';
import { tvpilJournal } from './tvpilJournal';

type CreateJournalMonitoringArgs = {
  spans: Span[];
  trackSpan: (payload: { from: TvpilEventName; timing: SpanTiming; to: TvpilEventName }) => void;
};

type Span = [TvpilEventName, TvpilEventName];
type SpanTiming = {
  duration: number;
  end: number;
  start: number;
};
type TvpilEventName = keyof TvpilJournalEvent;

/**
 * Groups spans by starting * ending point for instant access later
 * @example
 * [
 *  ['BootStart', 'BootEnd'],
 *  ['BootStart', 'GetUserStart'],
 *  ['GetUserStart', 'GetUserEnd'],
 * ]
 *  produces the following output
 * {
 *  startingPointGroup: {
 *    'BootStart': ['BootEnd', 'GetUserStart'],
 *    'GetUserStart': ['GetUserEnd']
 *  },
 *  endingPointGroup: {
 *    'BootEnd': ['BootStart'],
 *    'GetUserStart': ['BootStart', 'GetUserStart']
 *  }
 * }
 */
function groupSpans(spans: Span[]) {
  const startingPointGroup = new Map<TvpilEventName, Set<TvpilEventName>>();
  const endingPointGroup = new Map<TvpilEventName, Set<TvpilEventName>>();

  for (let i = 0; i < spans.length; i++) {
    const [startingPoint, endingPoint] = spans[i];

    addOrUpdate(startingPointGroup, startingPoint, endingPoint);
    addOrUpdate(endingPointGroup, endingPoint, startingPoint);
  }

  return { startingPointGroup, endingPointGroup };
}

function addOrUpdate(group: Map<TvpilEventName, Set<TvpilEventName>>, key: TvpilEventName, value: TvpilEventName) {
  const list = group.get(key);

  if (list) {
    list.add(value);
    return;
  }

  group.set(key, new Set<TvpilEventName>([value]));
}

export function createJournalMonitoring({ spans, trackSpan }: CreateJournalMonitoringArgs) {
  const { startingPointGroup, endingPointGroup } = groupSpans(spans);
  const events = new Map<TvpilEventName, JournalEvent<TvpilJournalEvent, TvpilJournalCategory>>();

  tvpilJournal.subscribe({
    handler: handleMessage,
  });

  function handleMessage(event: JournalEvent<TvpilJournalEvent, TvpilJournalCategory>) {
    // processing all spans where current event is the ending point
    const startingPoints = endingPointGroup.get(event.name);

    startingPoints?.forEach((start) => {
      const startingPoint = events.get(start);

      if (!startingPoint) {
        return;
      }

      trackSpan({
        from: startingPoint.name,
        to: event.name,
        timing: {
          start: startingPoint.timestamp,
          end: event.timestamp,
          duration: event.timestamp - startingPoint.timestamp,
        },
      });

      events.set(event.name, event);
    });

    // if there is a span where current event is the starting point track event
    if (startingPointGroup.has(event.name)) {
      events.set(event.name, event);
    }
  }
}
